import { type Hex, mix } from "@/color/oklch";
import type { SyntaxStyle } from "@/model/theme";
import type { SyntaxKey } from "@/model/tokens";
import { type ExportContext, type Exporter, GENERATED_BY } from "./types";

/** Color slots written into the Lua `c` table; groups refer to them by name. */
type Slot =
  | SyntaxKey
  | "bg"
  | "fg"
  | "panel"
  | "cursorline"
  | "linenr"
  | "linenr_active"
  | "selection"
  | "nontext"
  | "border"
  | "match_paren"
  | "search"
  | "cur_search"
  | "accent"
  | "status_bg"
  | "status_fg"
  | "pmenu"
  | "pmenu_sel"
  | "tab_inactive"
  | "error"
  | "warning"
  | "info"
  | "hint"
  | "ok"
  | "diff_add"
  | "diff_change"
  | "diff_delete"
  | "diff_text"
  | "git_add"
  | "git_change"
  | "git_delete"
  | "error_bg"
  | "warning_bg"
  | "info_bg"
  | "hint_bg"
  | "ok_bg"
  | "muted"
  | "subtle"
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "cyan"
  | "blue"
  | "purple"
  | "pink";

interface Hl {
  fg?: Slot;
  bg?: Slot;
  sp?: Slot;
  link?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  undercurl?: boolean;
  strikethrough?: boolean;
  reverse?: boolean;
}

function slots(ctx: ExportContext): Record<Slot, Hex> {
  const { color } = ctx;
  const bg = color("ui.editorBg");
  const virt = (h: Hex) => mix(bg, h, 0.12);
  const out: Partial<Record<Slot, Hex>> = {
    bg,
    fg: color("ui.editorFg"),
    panel: color("ui.panelBg"),
    cursorline: color("ui.cursorLine"),
    linenr: color("ui.lineNr"),
    linenr_active: color("ui.lineNrActive"),
    selection: color("ui.selection"),
    nontext: color("ui.nonText"),
    border: color("ui.border"),
    match_paren: color("ui.matchParen"),
    search: color("ui.searchBg"),
    cur_search: color("ui.curSearchBg"),
    accent: color("ui.accent"),
    status_bg: color("ui.statusBg"),
    status_fg: color("ui.statusFg"),
    pmenu: color("ui.pmenuBg"),
    pmenu_sel: color("ui.pmenuSel"),
    tab_inactive: color("ui.tabInactiveBg"),
    error: color("ui.error"),
    warning: color("ui.warning"),
    info: color("ui.info"),
    hint: color("ui.hint"),
    ok: color("ui.ok"),
    diff_add: color("ui.diffAdd"),
    diff_change: color("ui.diffChange"),
    diff_delete: color("ui.diffDelete"),
    diff_text: color("ui.diffText"),
    git_add: color("ui.gitAdd"),
    git_change: color("ui.gitChange"),
    git_delete: color("ui.gitDelete"),
    error_bg: virt(color("ui.error")),
    warning_bg: virt(color("ui.warning")),
    info_bg: virt(color("ui.info")),
    hint_bg: virt(color("ui.hint")),
    ok_bg: virt(color("ui.ok")),
    muted: color("palette.fg3"),
    subtle: color("palette.fg2"),
    red: color("palette.red"),
    orange: color("palette.orange"),
    yellow: color("palette.yellow"),
    green: color("palette.green"),
    cyan: color("palette.cyan"),
    blue: color("palette.blue"),
    purple: color("palette.purple"),
    pink: color("palette.pink"),
  };
  for (const [key, value] of Object.entries(ctx.resolved.colors)) {
    if (key.startsWith("syntax.")) out[key.slice(7) as SyntaxKey] = value;
  }
  return out as Record<Slot, Hex>;
}

function groups(styles: Record<SyntaxKey, SyntaxStyle>): [string, Hl][] {
  const s = (key: SyntaxKey, extra: Hl = {}): Hl => ({ fg: key, ...styles[key], ...extra });
  const diag = (["Error", "Warn", "Info", "Hint", "Ok"] as const).flatMap(
    (name): [string, Hl][] => {
      const slot = (
        { Error: "error", Warn: "warning", Info: "info", Hint: "hint", Ok: "ok" } as const
      )[name];
      return [
        [`Diagnostic${name}`, { fg: slot }],
        [`DiagnosticVirtualText${name}`, { fg: slot, bg: `${slot}_bg` }],
        [`DiagnosticUnderline${name}`, { sp: slot, undercurl: true }],
        [`DiagnosticFloating${name}`, { fg: slot }],
        [`DiagnosticSign${name}`, { fg: slot }],
      ];
    },
  );
  return [
    // Editor UI
    ["Normal", { fg: "fg", bg: "bg" }],
    ["NormalNC", { link: "Normal" }],
    ["NormalFloat", { fg: "fg", bg: "panel" }],
    ["FloatBorder", { fg: "border", bg: "panel" }],
    ["FloatTitle", { fg: "accent", bg: "panel", bold: true }],
    ["ColorColumn", { bg: "cursorline" }],
    ["Conceal", { fg: "muted" }],
    ["Cursor", { fg: "bg", bg: "fg" }],
    ["lCursor", { link: "Cursor" }],
    ["CursorIM", { link: "Cursor" }],
    ["CursorColumn", { bg: "cursorline" }],
    ["CursorLine", { bg: "cursorline" }],
    ["CursorLineNr", { fg: "linenr_active", bold: true }],
    ["LineNr", { fg: "linenr" }],
    ["SignColumn", { fg: "linenr" }],
    ["FoldColumn", { fg: "linenr" }],
    ["Folded", { fg: "muted", bg: "cursorline" }],
    ["Directory", { fg: "accent" }],
    ["EndOfBuffer", { fg: "nontext" }],
    ["ErrorMsg", { fg: "error" }],
    ["WarningMsg", { fg: "warning" }],
    ["MoreMsg", { fg: "ok" }],
    ["ModeMsg", { fg: "subtle", bold: true }],
    ["Question", { fg: "accent" }],
    ["Search", { fg: "fg", bg: "search" }],
    ["IncSearch", { fg: "bg", bg: "cur_search" }],
    ["CurSearch", { link: "IncSearch" }],
    ["Substitute", { fg: "bg", bg: "red" }],
    ["MatchParen", { bg: "match_paren", bold: true }],
    ["NonText", { fg: "nontext" }],
    ["Whitespace", { fg: "nontext" }],
    ["SpecialKey", { fg: "nontext" }],
    ["Pmenu", { fg: "fg", bg: "pmenu" }],
    ["PmenuSel", { bg: "pmenu_sel", bold: true }],
    ["PmenuSbar", { bg: "pmenu" }],
    ["PmenuThumb", { bg: "border" }],
    ["PmenuKind", { fg: "type", bg: "pmenu" }],
    ["PmenuExtra", { fg: "muted", bg: "pmenu" }],
    ["StatusLine", { fg: "status_fg", bg: "status_bg" }],
    ["StatusLineNC", { fg: "muted", bg: "panel" }],
    ["TabLine", { fg: "muted", bg: "tab_inactive" }],
    ["TabLineFill", { bg: "tab_inactive" }],
    ["TabLineSel", { fg: "fg", bg: "bg", bold: true }],
    ["Title", s("heading", { bold: true })],
    ["Visual", { bg: "selection" }],
    ["VisualNOS", { link: "Visual" }],
    ["WinSeparator", { fg: "border" }],
    ["VertSplit", { link: "WinSeparator" }],
    ["WildMenu", { link: "PmenuSel" }],
    ["WinBar", { fg: "subtle", bold: true }],
    ["WinBarNC", { fg: "muted" }],
    ["QuickFixLine", { bg: "cursorline", bold: true }],
    ["SpellBad", { sp: "error", undercurl: true }],
    ["SpellCap", { sp: "warning", undercurl: true }],
    ["SpellLocal", { sp: "info", undercurl: true }],
    ["SpellRare", { sp: "hint", undercurl: true }],
    ["DiffAdd", { bg: "diff_add" }],
    ["DiffChange", { bg: "diff_change" }],
    ["DiffDelete", { fg: "git_delete", bg: "diff_delete" }],
    ["DiffText", { bg: "diff_text" }],
    ["Added", { fg: "git_add" }],
    ["Changed", { fg: "git_change" }],
    ["Removed", { fg: "git_delete" }],
    ["diffAdded", { fg: "git_add" }],
    ["diffRemoved", { fg: "git_delete" }],
    ["diffChanged", { fg: "git_change" }],
    ["diffFile", { fg: "accent", bold: true }],
    ["diffLine", { fg: "muted" }],
    ["healthError", { fg: "error" }],
    ["healthSuccess", { fg: "ok" }],
    ["healthWarning", { fg: "warning" }],
    // Legacy syntax
    ["Comment", s("comment")],
    ["Constant", s("constant")],
    ["String", s("string")],
    ["Character", s("string")],
    ["Number", s("number")],
    ["Float", s("number")],
    ["Boolean", s("boolean")],
    ["Identifier", s("variable")],
    ["Function", s("function")],
    ["Statement", s("keyword")],
    ["Conditional", s("control")],
    ["Repeat", s("control")],
    ["Label", s("keyword")],
    ["Operator", s("operator")],
    ["Keyword", s("keyword")],
    ["Exception", s("control")],
    ["PreProc", s("macro")],
    ["Include", s("keyword")],
    ["Define", s("macro")],
    ["Macro", s("macro")],
    ["PreCondit", s("macro")],
    ["Type", s("type")],
    ["StorageClass", s("keyword")],
    ["Structure", s("type")],
    ["Typedef", s("type")],
    ["Special", s("escape")],
    ["SpecialChar", s("escape")],
    ["Tag", s("tag")],
    ["Delimiter", s("punctuation")],
    ["SpecialComment", s("comment")],
    ["Debug", { fg: "red" }],
    ["Underlined", { underline: true }],
    ["Error", { fg: "error" }],
    ["Todo", { fg: "bg", bg: "yellow", bold: true }],
    // Tree-sitter
    ["@variable", s("variable")],
    ["@variable.builtin", s("builtin")],
    ["@variable.parameter", s("parameter")],
    ["@variable.parameter.builtin", s("builtin")],
    ["@variable.member", s("property")],
    ["@property", s("property")],
    ["@constant", s("constant")],
    ["@constant.builtin", s("constant")],
    ["@constant.macro", s("macro")],
    ["@module", s("namespace")],
    ["@module.builtin", s("builtin")],
    ["@label", s("keyword")],
    ["@string", s("string")],
    ["@string.documentation", s("string")],
    ["@string.escape", s("escape")],
    ["@string.regexp", s("regex")],
    ["@string.special", s("escape")],
    ["@string.special.symbol", s("constant")],
    ["@string.special.url", s("link")],
    ["@string.special.path", s("string")],
    ["@character", s("string")],
    ["@character.special", s("escape")],
    ["@boolean", s("boolean")],
    ["@number", s("number")],
    ["@number.float", s("number")],
    ["@type", s("type")],
    ["@type.builtin", s("type")],
    ["@type.definition", s("type")],
    ["@attribute", s("attribute")],
    ["@attribute.builtin", s("builtin")],
    ["@function", s("function")],
    ["@function.builtin", s("builtin")],
    ["@function.call", s("function")],
    ["@function.macro", s("macro")],
    ["@function.method", s("method")],
    ["@function.method.call", s("method")],
    ["@constructor", s("type")],
    ["@operator", s("operator")],
    ["@keyword", s("keyword")],
    ["@keyword.coroutine", s("control")],
    ["@keyword.function", s("keyword")],
    ["@keyword.operator", s("keyword")],
    ["@keyword.import", s("keyword")],
    ["@keyword.type", s("keyword")],
    ["@keyword.modifier", s("keyword")],
    ["@keyword.repeat", s("control")],
    ["@keyword.return", s("control")],
    ["@keyword.debug", s("builtin")],
    ["@keyword.exception", s("control")],
    ["@keyword.conditional", s("control")],
    ["@keyword.conditional.ternary", s("operator")],
    ["@keyword.directive", s("macro")],
    ["@keyword.directive.define", s("macro")],
    ["@punctuation.delimiter", s("punctuation")],
    ["@punctuation.bracket", s("punctuation")],
    ["@punctuation.special", s("escape")],
    ["@comment", s("comment")],
    ["@comment.documentation", s("comment")],
    ["@comment.error", { fg: "bg", bg: "error", bold: true }],
    ["@comment.warning", { fg: "bg", bg: "warning", bold: true }],
    ["@comment.todo", { fg: "bg", bg: "info", bold: true }],
    ["@comment.note", { fg: "bg", bg: "hint", bold: true }],
    ["@markup.strong", { bold: true }],
    ["@markup.italic", { italic: true }],
    ["@markup.strikethrough", { strikethrough: true }],
    ["@markup.underline", { underline: true }],
    ["@markup.heading", s("heading", { bold: true })],
    ["@markup.quote", s("comment", { italic: true })],
    ["@markup.math", s("constant")],
    ["@markup.link", s("link")],
    ["@markup.link.label", s("link", { underline: false })],
    ["@markup.link.url", s("link", { underline: true })],
    ["@markup.raw", s("string")],
    ["@markup.list", s("punctuation")],
    ["@markup.list.checked", { fg: "ok" }],
    ["@markup.list.unchecked", { fg: "muted" }],
    ["@tag", s("tag")],
    ["@tag.builtin", s("tag")],
    ["@tag.attribute", s("attribute")],
    ["@tag.delimiter", s("punctuation")],
    ["@diff.plus", { fg: "git_add" }],
    ["@diff.minus", { fg: "git_delete" }],
    ["@diff.delta", { fg: "git_change" }],
    // LSP semantic tokens (link so Tree-sitter and LSP agree)
    ["@lsp.type.class", { link: "@type" }],
    ["@lsp.type.decorator", { link: "@attribute" }],
    ["@lsp.type.enum", { link: "@type" }],
    ["@lsp.type.enumMember", { link: "@constant" }],
    ["@lsp.type.function", { link: "@function" }],
    ["@lsp.type.interface", { link: "@type" }],
    ["@lsp.type.macro", { link: "@function.macro" }],
    ["@lsp.type.method", { link: "@function.method" }],
    ["@lsp.type.namespace", { link: "@module" }],
    ["@lsp.type.parameter", { link: "@variable.parameter" }],
    ["@lsp.type.property", { link: "@property" }],
    ["@lsp.type.struct", { link: "@type" }],
    ["@lsp.type.type", { link: "@type" }],
    ["@lsp.type.typeParameter", { link: "@type.definition" }],
    ["@lsp.type.variable", {}],
    ["@lsp.mod.deprecated", { strikethrough: true }],
    ["@lsp.typemod.function.defaultLibrary", { link: "@function.builtin" }],
    ["@lsp.typemod.variable.defaultLibrary", { link: "@variable.builtin" }],
    ["@lsp.typemod.variable.readonly", { link: "@constant" }],
    ["LspReferenceText", { bg: "selection" }],
    ["LspReferenceRead", { bg: "selection" }],
    ["LspReferenceWrite", { bg: "selection", underline: true }],
    ["LspInlayHint", { fg: "muted", bg: "cursorline" }],
    ["LspCodeLens", { fg: "muted" }],
    ["LspSignatureActiveParameter", { bold: true, underline: true }],
    // Diagnostics
    ...diag,
    ["DiagnosticUnnecessary", { fg: "muted" }],
    ["DiagnosticDeprecated", { strikethrough: true }],
    // gitsigns
    ["GitSignsAdd", { fg: "git_add" }],
    ["GitSignsChange", { fg: "git_change" }],
    ["GitSignsDelete", { fg: "git_delete" }],
    ["GitSignsCurrentLineBlame", { fg: "muted" }],
    // snacks.nvim (picker, explorer, dashboard, notifier, indent)
    ["SnacksPickerMatch", { fg: "accent", bold: true }],
    ["SnacksPickerDir", { fg: "muted" }],
    ["SnacksPickerPrompt", { fg: "accent" }],
    ["SnacksPickerBorder", { link: "FloatBorder" }],
    ["SnacksPickerTitle", { link: "FloatTitle" }],
    ["SnacksPickerSelected", { fg: "accent" }],
    ["SnacksIndent", { fg: "nontext" }],
    ["SnacksIndentScope", { fg: "border" }],
    ["SnacksDashboardHeader", { fg: "accent" }],
    ["SnacksDashboardIcon", { fg: "accent" }],
    ["SnacksDashboardKey", { fg: "orange" }],
    ["SnacksDashboardDesc", { fg: "fg" }],
    ["SnacksDashboardFooter", { fg: "muted" }],
    ["SnacksNotifierInfo", { fg: "info" }],
    ["SnacksNotifierWarn", { fg: "warning" }],
    ["SnacksNotifierError", { fg: "error" }],
    ["SnacksNotifierBorderInfo", { fg: "info" }],
    ["SnacksNotifierBorderWarn", { fg: "warning" }],
    ["SnacksNotifierBorderError", { fg: "error" }],
    // blink.cmp
    ["BlinkCmpMenu", { link: "Pmenu" }],
    ["BlinkCmpMenuBorder", { link: "FloatBorder" }],
    ["BlinkCmpMenuSelection", { link: "PmenuSel" }],
    ["BlinkCmpLabelMatch", { fg: "accent", bold: true }],
    ["BlinkCmpLabelDeprecated", { fg: "muted", strikethrough: true }],
    ["BlinkCmpGhostText", { fg: "muted" }],
    ["BlinkCmpDoc", { link: "NormalFloat" }],
    ["BlinkCmpDocBorder", { link: "FloatBorder" }],
    ["BlinkCmpKind", { fg: "type" }],
    ["BlinkCmpKindFunction", { fg: "function" }],
    ["BlinkCmpKindMethod", { fg: "method" }],
    ["BlinkCmpKindVariable", { fg: "variable" }],
    ["BlinkCmpKindField", { fg: "property" }],
    ["BlinkCmpKindProperty", { fg: "property" }],
    ["BlinkCmpKindKeyword", { fg: "keyword" }],
    ["BlinkCmpKindConstant", { fg: "constant" }],
    ["BlinkCmpKindModule", { fg: "namespace" }],
    ["BlinkCmpKindSnippet", { fg: "pink" }],
    ["BlinkCmpKindText", { fg: "subtle" }],
    // which-key, noice, flash, trouble, bufferline, mini.icons
    ["WhichKey", { fg: "accent" }],
    ["WhichKeyGroup", { fg: "keyword" }],
    ["WhichKeyDesc", { fg: "fg" }],
    ["WhichKeySeparator", { fg: "muted" }],
    ["WhichKeyNormal", { link: "NormalFloat" }],
    ["WhichKeyBorder", { link: "FloatBorder" }],
    ["NoiceCmdlinePopupBorder", { fg: "accent" }],
    ["NoiceCmdlinePopupTitle", { fg: "accent" }],
    ["NoiceCmdlineIcon", { fg: "accent" }],
    ["FlashLabel", { fg: "bg", bg: "pink", bold: true }],
    ["FlashMatch", { fg: "fg", bg: "selection" }],
    ["FlashCurrent", { fg: "bg", bg: "cur_search" }],
    ["FlashBackdrop", { fg: "muted" }],
    ["TroubleText", { fg: "fg" }],
    ["TroubleCount", { fg: "purple", bg: "cursorline" }],
    ["BufferLineFill", { bg: "tab_inactive" }],
    ["BufferLineBufferSelected", { fg: "fg", bold: true }],
    ["BufferLineIndicatorSelected", { fg: "accent" }],
    ["MiniIconsAzure", { fg: "blue" }],
    ["MiniIconsBlue", { fg: "blue" }],
    ["MiniIconsCyan", { fg: "cyan" }],
    ["MiniIconsGreen", { fg: "green" }],
    ["MiniIconsGrey", { fg: "subtle" }],
    ["MiniIconsOrange", { fg: "orange" }],
    ["MiniIconsPurple", { fg: "purple" }],
    ["MiniIconsRed", { fg: "red" }],
    ["MiniIconsYellow", { fg: "yellow" }],
    // Telescope, indent-blankline (non-LazyVim setups)
    ["TelescopeBorder", { link: "FloatBorder" }],
    ["TelescopeNormal", { link: "NormalFloat" }],
    ["TelescopeSelection", { link: "PmenuSel" }],
    ["TelescopeMatching", { fg: "accent", bold: true }],
    ["IblIndent", { fg: "nontext" }],
    ["IblScope", { fg: "border" }],
  ];
}

const LUA_RESERVED = new Set(
  "and break do else elseif end false for function goto if in local nil not or repeat return then true until while".split(
    " ",
  ),
);

function isLuaName(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !LUA_RESERVED.has(name);
}

/** A table key: `name` or `["@name"]`. */
function luaKey(name: string): string {
  return isLuaName(name) ? name : `["${name}"]`;
}

/** A field access on the `c` table: `c.name` or `c["function"]`. */
function slotRef(slot: string): string {
  return isLuaName(slot) ? `c.${slot}` : `c["${slot}"]`;
}

function luaHl(hl: Hl): string {
  if (hl.link) return `{ link = "${hl.link}" }`;
  const parts: string[] = [];
  if (hl.fg) parts.push(`fg = ${slotRef(hl.fg)}`);
  if (hl.bg) parts.push(`bg = ${slotRef(hl.bg)}`);
  if (hl.sp) parts.push(`sp = ${slotRef(hl.sp)}`);
  for (const flag of [
    "bold",
    "italic",
    "underline",
    "undercurl",
    "strikethrough",
    "reverse",
  ] as const) {
    if (hl[flag]) parts.push(`${flag} = true`);
  }
  return parts.length ? `{ ${parts.join(", ")} }` : "{}";
}

export function neovimLua(ctx: ExportContext): string {
  const { theme, appName, color } = ctx;
  const c = slots(ctx);
  const width = Math.max(...Object.keys(c).map((k) => luaKey(k).length));
  const slotLines = Object.entries(c).map(([k, v]) => `  ${luaKey(k).padEnd(width)} = "${v}",`);
  const hlLines = groups(ctx.resolved.styles).map(
    ([name, hl]) => `  ${luaKey(name)} = ${luaHl(hl)},`,
  );
  const terminal = Array.from({ length: 16 }, (_, i) => `"${color(`terminal.ansi${i}`)}"`);
  return `-- ${appName}: "${theme.name}" (${theme.variant}), ${GENERATED_BY}
-- Standalone colorscheme: no plugin required. Load with :colorscheme ${appName}

vim.cmd("highlight clear")
if vim.fn.exists("syntax_on") == 1 then
  vim.cmd("syntax reset")
end
vim.o.background = "${theme.variant}"
vim.o.termguicolors = true
vim.g.colors_name = "${appName}"

local c = {
${slotLines.join("\n")}
}

local groups = {
${hlLines.join("\n")}
}

for name, spec in pairs(groups) do
  vim.api.nvim_set_hl(0, name, spec)
end

local terminal = { ${terminal.join(", ")} }
for i, hex in ipairs(terminal) do
  vim.g["terminal_color_" .. (i - 1)] = hex
end
`;
}

export const neovim: Exporter = {
  id: "neovim",
  label: "Neovim",
  app: true,
  description: "Standalone Lua colorscheme: core UI, Tree-sitter, LSP and LazyVim plugins.",
  run: (ctx) => {
    const { appName } = ctx;
    return {
      files: [
        {
          path: `~/.config/nvim/colors/${appName}.lua`,
          filename: `${appName}.lua`,
          language: "lua",
          content: neovimLua(ctx),
        },
      ],
      steps: [
        `Create \`~/.config/nvim/colors/${appName}.lua\` with the file below (make the \`colors\` directory if needed).`,
        `LazyVim: in \`lua/plugins/colorscheme.lua\`, add \`{ "LazyVim/LazyVim", opts = { colorscheme = "${appName}" } }\` to the returned list. Leave other colorscheme plugin specs installed unless asked to remove them. Plain Neovim: put \`vim.cmd.colorscheme("${appName}")\` at the end of \`init.lua\`.`,
        `Restart Neovim, or run \`:colorscheme ${appName}\` in a running instance.`,
      ],
      verify: [
        `nvim --headless "+colorscheme ${appName}" "+lua assert(vim.g.colors_name == '${appName}')" +qa`,
      ],
      notes: [
        "lualine's default `auto` theme reads these highlight groups, so the status line follows the theme without extra config.",
      ],
    };
  },
};
